import { useCallback, useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Ban, Trash2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorText } from "@/lib/api";

const SLOTS = Array.from({ length: 12 }, (_, i) => {
  const h = i + 9;
  return [`${h}:00`, `${h - 12 === 0 ? 12 : h - 12}:00 ${h < 12 ? "AM" : "PM"}`];
});

export const ScheduleTab = () => {
  const [blocks, setBlocks] = useState(null);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/blocks")
    .then((r) => setBlocks(Array.isArray(r.data) ? r.data : []))
    .catch(() => setBlocks([])), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!date) { toast.error("Pick a date to close."); return; }
    setBusy(true);
    try {
      await api.post("/admin/blocks", { date: format(date, "yyyy-MM-dd"), time: time === "all-day" ? null : time, reason });
      toast.success(time ? "Time blocked." : "Whole day closed.");
      setTime(""); setReason("");
      load();
    } catch (e) { toast.error(errorText(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/blocks/${id}`); load(); toast.success("Reopened."); }
    catch (e) { toast.error(errorText(e)); }
  };

  return (
    <section>
      <p className="owner-hint">Closed days and times disappear from the booking page instantly. Whole day closes every slot from 9 AM to 8 PM.</p>
      <div className="owner-card schedule-form" data-testid="schedule-form">
        <div className="schedule-row">
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" className="st-control" data-testid="schedule-date-button">
                <span>{date ? format(date, "EEE, MMM d") : "Pick a date"}</span>
                <Ban size={16} />
              </button>
            </PopoverTrigger>
            <PopoverContent className="lux-pop w-auto p-0" data-lenis-prevent>
              <Calendar mode="single" selected={date} onSelect={setDate} initialFocus
                fromDate={new Date(Date.now() + 24 * 60 * 60 * 1000)}
                toDate={new Date(Date.now() + 91 * 24 * 60 * 60 * 1000)} />
            </PopoverContent>
          </Popover>
          <Select value={time} onValueChange={setTime}>
            <SelectTrigger className="st-control" data-testid="schedule-time-select">
              <SelectValue placeholder="All day" />
            </SelectTrigger>
            <SelectContent className="lux-menu" data-lenis-prevent>
              <SelectItem value="all-day" className="lux-item">All day (9 AM–8 PM)</SelectItem>
              {SLOTS.map(([v, label]) => <SelectItem key={v} value={v} className="lux-item">{label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <input className="st-control" placeholder="Reason (just for you — e.g. 'vacation')" value={reason}
          onChange={(e) => setReason(e.target.value)} maxLength={140} data-testid="schedule-reason-input" />
        <button type="button" className="cta sm" onClick={add} disabled={busy} data-testid="schedule-add-button">
          {busy ? "Closing…" : "Close it off"}
        </button>
      </div>
      {blocks === null ? <p className="owner-empty">Loading…</p> : !blocks.length ? (
        <p className="owner-empty" data-testid="schedule-empty">Nothing closed off right now.</p>
      ) : (
        <div className="schedule-list">
          {blocks.map((b) => (
            <div key={b.id} className="owner-card schedule-item" data-testid={`schedule-item-${b.id}`}>
              <div>
                <b>{format(parseISO(b.date), "EEE, MMM d")}</b>
                <span>{b.time ? `${SLOTS.find(([v]) => v === b.time)?.[1]} only` : "All day"}</span>
                {b.reason ? <em> — {b.reason}</em> : null}
              </div>
              <button type="button" className="icon-btn" onClick={() => remove(b.id)} aria-label="Reopen this time"
                data-testid={`schedule-remove-${b.id}`}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
